import { type SearchResultItem, SearchResultItemType } from '@/@types/search'
import { css } from '@emotion/react'
import { Link } from '@tanstack/react-router'
import { compact } from 'lodash-es'
import Mark from 'mark.js'
import { AnimatePresence, motion } from 'motion/react'
import { type HTMLAttributes, useCallback, useEffect, useRef } from 'react'
import Avatar from '../Avatar'
import PageLoading from '../PageLoading'
import ScreenName from '../ScreenName'
import PostSearchResultItem from './SearchResultItem/Post'

export interface SearchResultProps extends HTMLAttributes<HTMLDivElement> {
	data: SearchResultItem[]
	highlightWords: string[]
	isLoading?: boolean
}

export default function SearchResult({
	data,
	highlightWords,
	isLoading,
	...props
}: SearchResultProps) {
	const ref = useRef<HTMLDivElement | null>(null)
	const renderRow = useCallback((row: SearchResultItem, index: number) => {
		switch (row.type) {
			case SearchResultItemType.SECTION_HEADER:
				return (
					<div
						key={row.title}
						css={css`
							color: var(--tint-primary);
							font-size: 16px;
							font-weight: 500;
							line-height: 25px;
							padding: 0 10px;
						`}
					>
						{row.title}
					</div>
				)

			case SearchResultItemType.SECTION_FOOTER:
				return (
					<div
						key={compact([row.url, index]).join('-')}
						css={css`
							margin: 16px 0;
							height: 0.5px;
							background-color: var(--tint-separator);
						`}
					/>
				)

			case SearchResultItemType.TOPIC:
				return (
					row.id && (
						<Link
							data-clickable-feedback="false"
							to="/topic/$id"
							params={{
								id: row.id,
							}}
							key={row.id}
							css={css`
								display: flex;
								gap: 10px;
								align-items: center;
								padding: 10px;
							`}
						>
							<img
								width={40}
								height={40}
								src={row.squarePicture?.smallPicUrl}
								css={css`
									border-radius: 8px;
									border: 1px solid var(--tint-pic-border);
									width: 40px;
									height: 40px;
								`}
							/>
							<div
								css={css`
									flex: 1;
									display: flex;
									flex-direction: column;
									gap: 2px;
								`}
							>
								<div
									css={css`
										color: var(--tint-primary);
									`}
								>
									{row.content}
								</div>
								<div
									css={css`
										color: var(--tint-tertiary);
										font-size: 12px;
										line-height: 18px;
									`}
								>
									{row.subscribersDescription}
								</div>
							</div>
						</Link>
					)
				)

			case SearchResultItemType.USER_SECTION:
				return (
					<div key="USER_SECTION">
						{row.items?.map((item) => (
							<Link
								to="/u/$username"
								params={{
									username: item.username,
								}}
								key={item.id}
								data-clickable-feedback="true"
								css={css`
									display: flex;
									align-items: center;
									gap: 10px;
									padding: 10px;
								`}
							>
								<Avatar as="div" data={item} />
								<div
									css={css`
										flex: 1;
										display: flex;
										flex-direction: column;
										gap: 2px;
									`}
								>
									<ScreenName data={item} readonly />
									<div
										css={css`
											color: var(--tint-tertiary);
											font-size: 12px;
											line-height: 18px;
										`}
									>
										{item.briefIntro}
									</div>
								</div>
							</Link>
						))}
					</div>
				)

			case SearchResultItemType.ORIGINAL_POST:
				return (
					<PostSearchResultItem
						key={row.id}
						id={row.id as string}
						css={css`
							padding: 10px;

							&:not(:last-of-type) {
								box-shadow: 0 0.5px 0 0 var(--tint-separator);
								padding-bottom: 10px;
							}
						`}
					/>
				)
		}
	}, [])

	useEffect(() => {
		const container = ref.current
		if (!container) {
			return
		}
		const instance = new Mark(container)
		instance.mark(highlightWords)

		return () => {
			instance.unmark()
		}
	}, [highlightWords])

	return (
		<AnimatePresence>
			{isLoading ? (
				<PageLoading
					css={css`
						transform: scale(1.5);
					`}
				/>
			) : (
				<motion.div
					initial={{
						opacity: 0,
					}}
					animate={{
						opacity: 1,
					}}
					exit={{
						opacity: 0,
					}}
					ref={ref}
					css={css`
						mark {
							color: var(--tint-warning);
							appearance: none;
							background-color: unset;
						}
					`}
				>
					{data.map(renderRow)}
				</motion.div>
			)}
		</AnimatePresence>
	)
}
